import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../features/auth/context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "../../i18n";

const publicLinks = [
  { to: "/products", key: "marketing.nav.features", fallback: "Funktionen" },
  { to: "/pricing", key: "marketing.nav.pricing", fallback: "Preise" },
  { to: "/documentation", key: "marketing.nav.help", fallback: "Hilfe-Center" },
  { to: "/#download", key: "marketing.nav.download", fallback: "Download" },
];

/** Marken-Logo: Ring + gefüllter Innenkreis in Akzentfarbe (Token, beide Themes). */
function BrandMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke="rgb(var(--primary))" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" fill="rgb(var(--primary))" />
    </svg>
  );
}

export default function PublicHeader() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, setLocale } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const tf = (key, fallback) => (t(key) === key ? fallback : t(key));

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  const handleLogoClick = () => {
    navigate(isAuthenticated ? "/dashboard" : "/");
  };

  /* DE/EN-Segment im Mono-Stil (Design 3c) */
  const langSegment = (
    <div className="flex gap-px font-mono text-[10px] font-semibold" role="group">
      <button
        onClick={() => setLocale("de")}
        aria-label={t("layout.authHeader.switchToGermanAria")}
        aria-pressed={locale === "de"}
        className={`px-1.5 py-1 rounded-l transition-colors duration-150 ${
          locale === "de"
            ? "bg-on-surface/10 text-on-surface"
            : "bg-on-surface/[.04] text-on-surface-faint hover:text-on-surface"
        }`}
      >
        DE
      </button>
      <button
        onClick={() => setLocale("en")}
        aria-label={t("layout.authHeader.switchToEnglishAria")}
        aria-pressed={locale === "en"}
        className={`px-1.5 py-1 rounded-r transition-colors duration-150 ${
          locale === "en"
            ? "bg-on-surface/10 text-on-surface"
            : "bg-on-surface/[.04] text-on-surface-faint hover:text-on-surface"
        }`}
      >
        EN
      </button>
    </div>
  );

  const themeToggle = (
    <button
      onClick={toggleTheme}
      aria-label={
        theme === "dark"
          ? t("layout.authHeader.switchToLightAria")
          : t("layout.authHeader.switchToDarkAria")
      }
      className="text-on-surface-variant hover:text-on-surface p-1.5 rounded-lg hover:bg-on-surface/[.06] transition-colors duration-150"
      title={
        theme === "dark"
          ? t("layout.authHeader.switchToLightAria")
          : t("layout.authHeader.switchToDarkAria")
      }
    >
      <span className="material-symbols-outlined text-lg leading-none">
        {theme === "dark" ? "dark_mode" : "light_mode"}
      </span>
    </button>
  );

  return (
    <nav
      aria-label={tf("layout.publicHeader.navAria", "Hauptnavigation")}
      className="fixed top-0 w-full z-50 bg-surface-nav border-b border-outline-variant"
    >
      <div className="flex items-center gap-7 h-16 px-6 md:px-14 max-w-[1440px] mx-auto">
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          aria-label={t("layout.publicHeader.homeAria")}
          className="flex items-center gap-2.5"
        >
          <BrandMark />
          <span className="text-base font-semibold leading-none text-on-surface">Customy</span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex gap-6 items-center">
          {publicLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-[13.5px] font-medium transition-colors duration-150 ${
                location.pathname === link.to
                  ? "text-on-surface"
                  : "text-on-surface-variant hover:text-on-surface"
              }`}
            >
              {tf(link.key, link.fallback)}
            </Link>
          ))}
        </div>

        {/* Right: Lang + Theme + Auth buttons (desktop) */}
        <div className="hidden md:flex items-center gap-3.5 ml-auto">
          {langSegment}
          {themeToggle}
          <Link
            to="/login"
            className="text-[13.5px] font-medium text-on-surface-variant hover:text-on-surface transition-colors duration-150 px-1"
          >
            {tf("marketing.nav.signIn", "Anmelden")}
          </Link>
          <Link
            to="/register"
            className="bg-primary text-on-primary hover:bg-primary-fixed font-semibold px-4 py-2 rounded-lg text-[13px] transition-colors duration-150"
          >
            {tf("marketing.nav.cta", "Kostenlos starten")}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden ml-auto text-on-surface-variant hover:text-on-surface p-2"
          onClick={() => setMobileOpen((p) => !p)}
          aria-label={t("layout.publicHeader.toggleNavAria")}
          aria-expanded={mobileOpen}
          aria-controls="public-mobile-menu"
        >
          <span className="material-symbols-outlined text-2xl leading-none">
            {mobileOpen ? "close" : "menu"}
          </span>
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          id="public-mobile-menu"
          className="md:hidden bg-surface-nav border-t border-outline-variant px-6 py-4 space-y-1"
        >
          {publicLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`flex min-h-11 items-center text-sm font-medium transition-colors duration-150 ${
                location.pathname === link.to
                  ? "text-on-surface"
                  : "text-on-surface-variant hover:text-on-surface"
              }`}
            >
              {tf(link.key, link.fallback)}
            </Link>
          ))}
          <div className="flex gap-3 pt-3 mt-2 border-t border-outline-variant items-center">
            {langSegment}
            {themeToggle}

            <div className="flex-1" />

            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="text-sm font-medium text-on-surface-variant hover:text-on-surface py-2 transition-colors duration-150"
            >
              {tf("marketing.nav.signIn", "Anmelden")}
            </Link>
            <Link
              to="/register"
              onClick={() => setMobileOpen(false)}
              className="bg-primary text-on-primary hover:bg-primary-fixed font-semibold px-4 py-2 rounded-lg text-[13px] transition-colors duration-150"
            >
              {tf("marketing.nav.cta", "Kostenlos starten")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
